import React from 'react'
import { EcommerceResponse, IEcommerceQuestionSchema } from '../../types'
import style from './PromoRow.module.scss'
import Card from './Card'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import { getMediumAssetPath } from '@aytm/lib/common/assets'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import AssetsGate from '@aytm/app/swidget/base/questions/Ecommerce/components/AssetsGate.tsx'
import { CardsSkeleton, PlateSkeleton } from './Skeleton'

interface IPromoRowProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  findability: boolean
  onAddToCart: (qnt: 1 | -1, index: number) => void
  onItemSelect: (index: number, bannerPlacement?: string) => void
  deliveryDates: number[]
}

const PromoHeader = ({ schema, isMobile }: { schema: IEcommerceQuestionSchema; isMobile: boolean }) => {
  const { t } = useSurveyLocale()
  const headerImage = isMobile ? schema.ad_image1 : schema.ad_image2
  if (!schema.ad_company_title && !schema.ad_company_description && !headerImage) return null
  return (
    <div className={style.header}>
      {headerImage && (
        <img src={getMediumAssetPath(headerImage)} className={style.logo} alt={isMobile ? 'Logo' : 'Banner'} />
      )}
      <div className={style.title}>
        {schema.ad_company_title}
        {schema.ad_company_description && <div className={style.description}>{schema.ad_company_description}</div>}
        {!isMobile && (
          <EmptyLink color="#0053e2">
            <div className={style.shopButton}>{t('questions', 'ec', 'ecommerce', 'shopNow')}</div>
          </EmptyLink>
        )}
      </div>
    </div>
  )
}

const PromoRow = ({ schema, response, findability, onAddToCart, onItemSelect, deliveryDates }: IPromoRowProps) => {
  const promoAnswers = schema.answers.filter(answer => answer.banner && !answer.na)
  if (promoAnswers.length === 0) return null
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  // The row's header and cards wait for their own pictures behind outlines of their own; the phone's
  // header and creative sit above the row and show as they load.
  const rowImages = [
    !viewport.mobile && schema.ad_image2 && getMediumAssetPath(schema.ad_image2),
    ...promoAnswers.map(answer => answer.image && getMediumAssetPath(answer.image, 'transparent'))
  ]
  const rowSkeleton = (
    <>
      {!viewport.mobile && <PlateSkeleton className={style.headerSkeleton} />}
      <CardsSkeleton count={promoAnswers.length} kind="banner" />
    </>
  )

  return (
    <div className={`${style.promoContainer} ${viewport.mobile ? style.mobile : ''}`} data-testid="SW-EC-Banner">
      <div className={`${style.content} ${viewport.mobile ? style.mobile : ''}`}>
        {viewport.mobile && <PromoHeader schema={schema} isMobile={true} />}
        {viewport.mobile && schema.ad_image2 && (
          <img src={getMediumAssetPath(schema.ad_image2)} className={style.adImage} alt="Banner" />
        )}
        <div className={style.promoRow}>
          <div className={style.promoRowItems}>
            <AssetsGate urls={rowImages} loader={rowSkeleton}>
              {!viewport.mobile && <PromoHeader schema={schema} isMobile={false} />}
              {promoAnswers.map(answer => (
                <Card
                  key={answer.id}
                  data-testid="SW-EC-BannerItem"
                  answer={answer}
                  banner={true}
                  bannerPlacement="banner"
                  response={response}
                  questionSchema={schema}
                  index={answer.index}
                  findability={findability}
                  disabled={false}
                  onAddToCart={onAddToCart}
                  onItemSelect={() => onItemSelect(answer.index, 'banner')}
                  deliveryDate={deliveryDates[answer.index]}
                />
              ))}
            </AssetsGate>
          </div>
          {viewport.mobile && (
            <div className={style.footer}>
              <EmptyLink color="#0053e2">{t('questions', 'ec', 'ecommerce', 'shopNow')}</EmptyLink>
              <div className={style.note}>
                <span className={style.noteText}>{t('questions', 'ec', 'walmart', 'sponsored')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PromoRow
